// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EventHandlerCallback = (...args: any[]) => void;

export type IEventDispatcher = {
	on<THandler extends EventHandlerCallback>(event: string, handler: THandler): () => void;
	off<THandler extends EventHandlerCallback>(event: string, handler: THandler): void;
	dispatch(event: string, ...args: unknown[]): void;
	dispatchAsync(event: string, ...args: unknown[]): Promise<void>;
};

export default class EventDispatcher implements IEventDispatcher {
	protected readonly _handlers: Map<string, EventHandlerCallback[]> = new Map<string, EventHandlerCallback[]>();

	public on<THandler extends EventHandlerCallback>(event: string, handler: THandler) {
		const handlers = this._handlers.get(event) ?? [];
		handlers.push(handler);
		this._handlers.set(event, handlers);

		return () => {
			this.off(event, handler);
		};
	}

	public off<THandler extends EventHandlerCallback>(event: string, handler: THandler) {
		const handlers = this._handlers.get(event) ?? [];
		const index = handlers.indexOf(handler);
		if (index > -1) {
			handlers.splice(index, 1);
		}
		this._handlers.set(event, handlers);
	}

	public dispatch(event: string, ...args: unknown[]) {
		const handlers = this._handlers.get(event) ?? [];
		for (const handler of handlers) {
			try {
				handler(...args);
			} catch (err) {
				console.error(`Error dispatching event ${event}`, err);
			}
		}
	}

	/**
	 * Awaitable variant of `dispatch`. Resolves once every handler (including
	 * async handlers that return a promise) has settled, and rejects if any
	 * handler throws or rejects. Use this when the caller needs to know whether
	 * the side effect actually happened — e.g. enqueuing onto a job queue,
	 * where a silently-dropped enqueue would otherwise look like a success.
	 */
	public async dispatchAsync(event: string, ...args: unknown[]): Promise<void> {
		const handlers = this._handlers.get(event) ?? [];
		await Promise.all(handlers.map((handler) => handler(...args)));
	}

	public dispose() {
		this._handlers.clear();
	}
}
